import {Component, Input, ViewChild} from '@angular/core';
import {ChartBaseComponent} from '../base/chart-base/chart-base.component';
import {LineChartComponent} from '../../../../shared/components/charts/line-chart/line-chart.component';
import {Subject, take, takeUntil} from 'rxjs';
import {LineChartDataModel} from '../../../../../models/line-chart-data.model';
import {BmiChartTableData} from '../../models/fetch-bmi-chart-table-data.model';
import {Store} from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import {CommonService} from '../../../../shared/services/common.service';
import {PrintService} from '../../../../shared/services/print.service';
import {bmiChartData, bmiChartTableData, bmiGoalFilter, adultBmiGoalFilter} from '../../core/progress.selectors';
import {FetchBmiChartDataRequest, FetchBmiChartTableDataRequest, FetchBmiGoalFilterDataRequest, FetchAdultBmiGoalFilterDataRequest} from '../../core/progress.actions';
import {Filter} from '../../../../../models/filter';
import {FormBuilder, FormControl} from '@angular/forms';
import moment from 'moment';
import {roles} from '../../../../shared/constants/common.constants';
import {profile} from '../../../../shared/core/shared.selectors';
import {ProgressService} from '../../core/progress.service';

@Component({
  selector: 'app-bmi-chart',
  templateUrl: './bmi-chart.component.html',
  styleUrls: ['./bmi-chart.component.css']
})
export class BmiChartComponent extends ChartBaseComponent {
  // for print
  @Input() printData: any;

  @ViewChild(LineChartComponent, {static: false}) lineChartComponent: LineChartComponent;
  onDestroy = new Subject<void>();
  chartData: {
    loading: boolean;
    data: LineChartDataModel
  };
  chartTableData: {
    loading: boolean;
    data: BmiChartTableData
  };
  fromDate: string;
  toDate: string;
  legendList = [
    {
      label: 'Coach',
      color: 'rgb(224, 79, 57)',
      isCoachEntered: true,
      checked: true,
      lineId: 'line_coach',
    }
  ]
  roleDecorators = {
    1: {
      label: 'Coach',
      color: 'rgb(224, 79, 57)'
    },
    2: {
      label: 'Family',
      color: 'rgb(39, 153, 137)'
    },
  }
  columns = [
    'addedBy',
    'recordedOn',
    //'sessions',
    'weight',
    'height',
    'bmi',
    'bmiperc',
    'lbsover',
    'percover',
  ];

  isCoachEntered: boolean = true;
  initialLoad = true;
  isFirstLoad = true;
  additionalText = '';
  additionalText2 = '';
  bmiDecimalPlaces = '.3'
  bmiGoalFilter: Filter;
  adultBmiGoalFilter: Filter;
  sortColumn = 'recordedOn';
  sortOrder: 'asc' | 'desc' = 'asc';
  form = this.formBuilder.group({
    bmiGoal: new FormControl(),
    chartType: new FormControl('bmi'),
  });
  selectedBMIGoal = null;
  defaultPercentile: number = null;
  defaultPercentileApplied = false;
  defaultPercentileLoading = false;
  changeInPercentOver: number = null;

  chartTypeOptions: any = [
    {
      value: 'bmi',
      label: 'BMI',
      required: false
    },
    {
      value: 'lbs',
      label: 'Pounds Over',
      required: true
    },
    {
      value: 'percent',
      label: 'Percent Over',
      required: true
    }
  ]

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private printService: PrintService,
    private formBuilder: FormBuilder,
    private progressService: ProgressService,
  ) {
    super();
  }

  override onDateChange(data: {
    fromDate: string;
    toDate: string
  }) {
    this.fromDate = data.fromDate;
    this.toDate = data.toDate;

    // Set default chart type to "Percent Over" for participants on first load
    if (this.isFirstLoad && !this.isAdult) {
      this.form.patchValue({chartType: 'percent'}, {emitEvent: false});
    }
    this.isFirstLoad = false;

    // For non-adult participants, wait for default percentile before first fetch
    if (!this.isAdult && !this.printData && !this.defaultPercentileApplied) {
      return;
    }

    // fetch chart details
    this.fetchChartDetails();
  }

  override onPrint(complete: () => void) {
    if (!this.chartData?.data) {
      this.commonService.notification('Nothing to print.', 'danger');
      complete();
      return;
    }

    let additionalText = '';
    if (this.userRole === roles.participant || this.isAdult) {
      const selectedChartType = this.chartTypeOptions.find((item) => item.value === this.form.controls.chartType.getRawValue());

      additionalText = ` - ${selectedChartType.label.toUpperCase()} - BMI Goal ${this.selectedBMIGoal.value}`
    }

    // this.commonService.printElement('bmi-chart-container');
    this.printService.open({
      component: BmiChartComponent,
      data: {
        fromDate: this.fromDate,
        toDate: this.toDate,
        isCoachEntered: this.isCoachEntered,
        memberId: this.memberId,
        groupId: this.groupId,
        bmiGoal: this.selectedBMIGoal,
        userName: this.getUserName(this.profile),
        title: this.getPageTitle({
          profile: this.profile,
          fromDate: this.fromDate,
          toDate: this.toDate,
          chartName: this.chartName,
        }),
        bmiGoalFilter: this.isAdult ? this.adultBmiGoalFilter : this.bmiGoalFilter,
        formValue: this.form.getRawValue(),
        userRole: this.userRole,
        additionalText,
      },
      onClose: complete,
    })
  }

  ngOnInit() {

    this.legendList.push(
      {
        label: this.isAdult ? 'Adult': 'Participant',
        color: 'rgb(39, 153, 137)',
        isCoachEntered: false,
        checked: false,
        lineId: 'line_participant',
      }
    );

    if (this.userRole === roles.adult) {
      this.columns = [
        'addedBy',
        'recordedOn',
        // 'session',
        'weight',
        'height',
        'bmi',
      ]
    }

    // For adults, show BMI and pounds over columns (no bmiperc)
    if (this.isAdult) {
      this.columns = [
        'addedBy',
        'recordedOn',
        'weight',
        'height',
        'bmi',
        'lbsover',
      ]

      // Remove "Percent Over" option for adults (not applicable)
      this.chartTypeOptions = this.chartTypeOptions.filter(option => option.value !== 'percent');
    }
    if (this.printData) {
      this.fromDate = this.printData['fromDate']
      this.toDate = this.printData['toDate']
      this.isCoachEntered = this.printData['isCoachEntered']
      this.memberId = this.printData['memberId']
      this.groupId = this.printData['groupId']
      this.selectedBMIGoal = this.printData['bmiGoal']
      this.userRole = this.printData['userRole']

      // Set appropriate BMI goal filter based on isAdult
      if (this.isAdult) {
        this.adultBmiGoalFilter = this.printData['bmiGoalFilter']
      } else {
        this.bmiGoalFilter = this.printData['bmiGoalFilter']
      }

      // set form values
      this.form.patchValue(this.printData['formValue']);

      // mark check boxes
      if (this.isCoachEntered !== null) {
        this.legendList.forEach((item) => {
          item.checked = item.isCoachEntered === this.isCoachEntered;
        })
      }
      this.additionalText = this.printData['additionalText'];

      // append date to title
      if (this.fromDate && this.toDate) {
        this.additionalText2 = `${moment(this.fromDate).format('MMM DD, YYYY')} - ${moment(this.toDate).format('MMM DD, YYYY')}`
      } else if (this.fromDate) {
        this.additionalText2 = `From ${moment(this.fromDate).format('MMM DD, YYYY')}`
      } else if (this.toDate) {
        this.additionalText2 = `Till ${moment(this.toDate).format('MMM DD, YYYY')}`
      }
    } else {
      // Fetch appropriate BMI goal filter based on isAdult flag
      if (this.isAdult) {
        this.store.dispatch(FetchAdultBmiGoalFilterDataRequest());

        this.store.select(adultBmiGoalFilter)
          .pipe(takeUntil(this.onDestroy))
          .subscribe((data) => {
            this.adultBmiGoalFilter = data;
            if (!this.adultBmiGoalFilter?.loading && this.adultBmiGoalFilter?.list?.length) {
              this.selectedBMIGoal = this.adultBmiGoalFilter.list.find(o => o.key == 0);
              this.form.patchValue({
                bmiGoal: this.selectedBMIGoal.key,
              }, {
                emitEvent: false,
              })
            }
          })

        this.form.controls.bmiGoal.valueChanges.pipe(takeUntil(this.onDestroy))
          .subscribe((value) => {
            this.selectedBMIGoal = this.adultBmiGoalFilter?.list?.find((item) => item.key == value);
            this.fetchChartDetails();
          })
      } else {
        this.store.dispatch(FetchBmiGoalFilterDataRequest());

        // Fetch default percentile from API
        this.defaultPercentileLoading = true;
        this.progressService.fetchBmiDefaultPercentile({
          ...(this.memberId ? {userId: this.memberId} : {}),
          ...(this.groupId ? {groupId: this.groupId} : {}),
        }).pipe(take(1), takeUntil(this.onDestroy))
          .subscribe((response) => {
            this.defaultPercentile = response?.data?.defaultPercentile || 95;
            this.defaultPercentileLoading = false;
            this.applyDefaultPercentile();
          });

        this.store.select(bmiGoalFilter)
          .pipe(takeUntil(this.onDestroy))
          .subscribe((data) => {
            this.bmiGoalFilter = data;
            if (!this.bmiGoalFilter?.loading && this.bmiGoalFilter?.list?.length) {
              this.applyDefaultPercentile();
            }
          })

        this.form.controls.bmiGoal.valueChanges.pipe(takeUntil(this.onDestroy))
          .subscribe((value) => {
            this.selectedBMIGoal = this.bmiGoalFilter?.list?.find((item) => item.key == value);
            this.fetchChartDetails();
          })
      }
    }
    this.store.select(bmiChartData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        this.chartData = this.getProcessedChartState(data);
        this.calculateChangeInPercentOver();

        // check is it for print
        if (!this.chartData?.loading && this.printData) {
          this.checkDataReadyForPrint();
        }
      });

    this.store.select(bmiChartTableData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        this.chartTableData = data;
        // check is it for print
        if (!data.loading && this.printData) {
          this.checkDataReadyForPrint();
        }

        if (!data.loading && data.data?.hasNext) {
          setTimeout(() => {
            // check window height for infinite scroll
            this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
              this.onScroll();
            });
          }, 0)
        }
      });

    if (this.printData) {
      this.fetchChartDetails();
    }

    this.form.controls.chartType.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.fetchChartDetails();
      });
  }

  fetchChartDetails() {
    const {chartType} = this.form.getRawValue();
    // fetch chart data
    this.store.dispatch(FetchBmiChartDataRequest({
      payload: {
        ...(this.memberId ? {userId: this.memberId} : {}),
        ...(this.groupId ? {groupId: this.groupId} : {}),
        from: this.fromDate ? moment(this.fromDate).format('YYYY-MM-DD') : this.fromDate,
        to: this.toDate ? moment(this.toDate).format('YYYY-MM-DD') : this.toDate,
        isCoachEntered: this.isCoachEntered,
        ...(this.isAdult ? {isAdult: true} : {}),
        ...(this.userRole === roles.adult && !this.isAdult ? {} : {
          bmiGoal:this.selectedBMIGoal?.key || (this.isAdult ? 0 : 95),
        }),
        ...(chartType ? {chartType} : {})
      },
      forPrint: !!this.printData,
    }))

    // fetch chart table data
    if (this.printData) {
      this.fetchTableData(1, -1);
    } else {
      this.fetchTableData();
    }
  }

  private applyDefaultPercentile() {
    if (this.defaultPercentileApplied || this.defaultPercentile === null) return;
    if (!this.bmiGoalFilter?.list?.length) return;

    this.defaultPercentileApplied = true;
    this.selectedBMIGoal = this.bmiGoalFilter.list.find(o => o.key == this.defaultPercentile)
      || this.bmiGoalFilter.list.find(o => o.key == 95);
    this.form.patchValue({
      bmiGoal: this.selectedBMIGoal.key,
    }, {
      emitEvent: false,
    });
    this.fetchChartDetails();
  }

  private fetchTableData(pageNumber = 1, limit = 10) {
    this.store.dispatch(FetchBmiChartTableDataRequest({
      payload: {
        ...(this.memberId ? {userId: this.memberId} : {}),
        ...(this.groupId ? {groupId: this.groupId} : {}),
        from: this.fromDate ? moment(this.fromDate).format('YYYY-MM-DD') : this.fromDate,
        to: this.toDate ? moment(this.toDate).format('YYYY-MM-DD') : this.toDate,
        isCoachEntered: this.isCoachEntered,
        ...(this.isAdult ? {isAdult: true} : {}),
        ...(this.userRole === roles.adult && !this.isAdult ? {} : {
          bmiGoal: this.selectedBMIGoal?.key || (this.isAdult ? 0 : 95),
        }),
        pageNumber,
        limit,
        sortColumn: this.sortColumn,
        sortOrder: this.sortOrder,
      },
      forPrint: !!this.printData,
    }))
  }

  onScroll() {
    if (!this.chartTableData?.loading && this.chartTableData?.data?.hasNext) {
      this.fetchTableData(this.chartTableData?.data?.pageNumber + 1, this.chartTableData?.data?.limit);
    }
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      // Toggle sort order
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to asc
      this.sortColumn = column;
      this.sortOrder = 'asc';
    }
    // Fetch data with new sorting
    if (this.printData) {
      this.fetchTableData(1, -1);
    } else {
      this.fetchTableData();
    }
  }

  onRoleToggle(item: any) {
    item.checked = !item.checked;
    // this.lineChartComponent.toggleLine(item.lineId);

    // check roles and fire fetch
    const selectedRoles = this.legendList.filter((item) => item.checked);
    if (selectedRoles.length === 2) {
      this.isCoachEntered = null;
      this.fetchChartDetails();
    } else if (selectedRoles.length === 1) {
      this.isCoachEntered = selectedRoles[0].isCoachEntered;
      this.fetchChartDetails();
    } else {
      this.chartData = null;
      this.chartTableData = null;
    }
  }

  checkDataReadyForPrint() {
    if (this.chartData?.data && this.chartTableData?.data?.list?.length) {
      if (this.initialLoad) {
        this.initialLoad = false;
        return
      }
      this.printService.start(this.printData?.title);
    }
  }

  private calculateChangeInPercentOver() {
    this.changeInPercentOver = null;
    if (this.form.controls.chartType.value !== 'percent') return;
    if (this.isCoachEntered !== true) return;
    if (!this.chartData?.data?.lines?.length) return;

    const coachLine = this.chartData.data.lines.find(line => line.lineId === 'line_coach');
    if (!coachLine?.data?.length || coachLine.data.length < 2) return;

    const sorted = [...coachLine.data].sort((a, b) => new Date(a.x as string).getTime() - new Date(b.x as string).getTime());
    const first = parseFloat(sorted[0].y as string);
    const last = parseFloat(sorted[sorted.length - 1].y as string);
    if (!isNaN(first) && !isNaN(last)) {
      this.changeInPercentOver = parseFloat((last - first).toFixed(1));
    }
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private getProcessedChartState(data) {
    if (!data?.data) {
      return data;
    }

    // Always ensure Y-axis bounds are set, especially for negative values (pounds over)
    const processedData = this.ensureChartYAxisBounds(data.data);

    if (!this.shouldSuppressAdultPoundsOverLines()) {
      return {
        ...data,
        data: processedData,
      };
    }

    return {
      ...data,
      data: this.createEmptyChartDataModel(processedData),
    };
  }

  private shouldSuppressAdultPoundsOverLines(): boolean {
    if (!this.isAdult) {
      return false;
    }

    const selectedChartType = this.form?.controls?.chartType?.getRawValue();
    if (selectedChartType !== 'lbs') {
      return false;
    }

    const goalKey = this.selectedBMIGoal?.key;
    if (goalKey === null || goalKey === undefined || goalKey === '') {
      return false;
    }

    if (typeof goalKey === 'string' && goalKey.toLowerCase() === 'none') {
      return true;
    }

    const numericGoalKey = Number(goalKey);
    return !Number.isNaN(numericGoalKey) && numericGoalKey === 0;
  }

  private createEmptyChartDataModel(chartData: LineChartDataModel): LineChartDataModel {
    const config = {
      x: {
        ...chartData.config.x,
      },
      y: {
        ...chartData.config.y,
      },
    };

    const originalPoints = (chartData.lines || []).flatMap((line) => line?.data || []);
    this.ensureXAxisBounds(config.x, originalPoints);
    this.ensureYAxisBounds(config.y, originalPoints);

    return {
      ...chartData,
      config,
      lines: [],
      zones: chartData.zones ? [] : chartData.zones,
    };
  }

  private ensureXAxisBounds(axisConfig: LineChartDataModel['config']['x'], points: any[]): void {
    if (!axisConfig) {
      return;
    }

    const values = points
      .map((point) => point?.x)
      .filter((value) => value !== null && value !== undefined && value !== '');

    if (!values.length) {
      return;
    }

    if (axisConfig.type === 'date') {
      const dates = values
        .map((value) => moment(value))
        .filter((value) => value?.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf());

      if (dates.length) {
        axisConfig.min = dates[0].toISOString();
        axisConfig.max = dates[dates.length - 1].toISOString();
      }
    } else {
      const numericValues = values
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value));

      if (numericValues.length) {
        axisConfig.min = Math.min(...numericValues);
        axisConfig.max = Math.max(...numericValues);
      }
    }
  }

  private ensureChartYAxisBounds(chartData: LineChartDataModel): LineChartDataModel {
    if (!chartData?.config?.y) {
      return chartData;
    }

    const config = {
      ...chartData.config,
      y: {
        ...chartData.config.y,
      },
    };

    const originalPoints = (chartData.lines || []).flatMap((line) => line?.data || []);
    this.ensureYAxisBounds(config.y, originalPoints);

    return {
      ...chartData,
      config,
    };
  }

  private ensureYAxisBounds(axisConfig: LineChartDataModel['config']['y'], points: any[]): void {
    if (!axisConfig) {
      return;
    }

    const values = points
      .map((point) => Number(point?.y))
      .filter((value) => !Number.isNaN(value));

    if (!values.length) {
      axisConfig.min = axisConfig.min ?? 0;
      axisConfig.max = axisConfig.max ?? 1;
      return;
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    // Add padding to ensure negative values are visible
    const padding = Math.max((maxValue - minValue) * 0.1, 1);
    
    axisConfig.min = minValue - padding;
    axisConfig.max = maxValue + padding;

    // If all values are the same, add some range
    if (axisConfig.min === axisConfig.max) {
      axisConfig.min = axisConfig.min - 1;
      axisConfig.max = axisConfig.max + 1;
    }
  }

  protected readonly length = length;
  protected readonly roles = roles;
}
